import Constant from '@/common/constant/Constant';
import TranslationConstant from '@/common/constant/TranslationConstant';
import localStorageUtil from '@/common/utils/localStorageUtil';
import translationStore from '@/common/utils/translationStore';
import Utils from '@/common/utils/utils';
import { invokeTranslationUpdateApi, MAX_TEXTS_PER_REQUEST } from '@/service/translationService';

/**
 * Translation calls for the markers of a user-authored HTML field (MLT-001-F3).
 *
 * data-tr  : same dictionary as tr() (usage UI = 画面文言, shared with the system UI, BR-7/AC-2).
 *            Hits already loaded for tr() are used as is; the rest goes to TRANSLATE_AUTO, which
 *            returns the stored rows and translates + stores the missing ones. The HTML's detected
 *            source language is passed along, so an HTML written in English is translated too.
 * ondmd    : TRANSLATE_ONDEMAND sourceType HTML_FIELD - glossary, usage metered, nothing stored.
 *
 * Fail-safe: every function resolves to a (possibly empty) dictionary; a missing entry simply
 * leaves the original text in the iframe.
 */

const getRequestIdentity = () => ({
    affiliationOrganization: localStorageUtil.getAffiliationOrganization(),
    userId: localStorageUtil.getUserDetailId()
});

/** Hits of the tr() cache, only when it holds translations of the same language pair. */
const getCachedUiTranslations = (sourceTexts, sourceLanguage, targetLanguage) => {
    const config = translationStore.getConfig();
    const isSamePair =
        config?.enabled &&
        config?.mainLanguage === targetLanguage &&
        sourceLanguage === Constant.TRANSLATION_SOURCE_LANGUAGE;
    if (!isSamePair) return {};

    const hits = {};
    sourceTexts.forEach(sourceText => {
        const translated = translationStore.get(Constant.TRANSLATION_USAGE.UI, sourceText);
        if (translated) hits[sourceText] = translated;
    });
    return hits;
};

const chunk = (list, size) => {
    const chunks = [];
    for (let index = 0; index < list.length; index += size) {
        chunks.push(list.slice(index, index + size));
    }
    return chunks;
};

/**
 * @returns {Promise<Object>} { [sourceText]: translatedText } for the data-tr markers.
 */
export const translateTrSources = async (sourceTexts, sourceLanguage, targetLanguage) => {
    if (Utils.isEmptyArray(sourceTexts) || !sourceLanguage || !targetLanguage) return {};

    const translations = getCachedUiTranslations(sourceTexts, sourceLanguage, targetLanguage);
    const missing = sourceTexts.filter(sourceText => !translations[sourceText]);
    if (missing.length === 0) return translations;

    const { affiliationOrganization, userId } = getRequestIdentity();
    if (!affiliationOrganization || !userId) return translations;

    const results = await Promise.all(
        chunk(missing, MAX_TEXTS_PER_REQUEST).map(sourceTextChunk =>
            invokeTranslationUpdateApi({
                affiliationOrganization,
                userId,
                actionType: Constant.TRANSLATION_ACTION.TRANSLATE_AUTO,
                requestBody: {
                    usage: Constant.TRANSLATION_USAGE.UI,
                    targetLanguage,
                    sourceLanguage,
                    sourceTexts: sourceTextChunk
                }
            })
        )
    );
    results.forEach(({ data }) => Object.assign(translations, data?.translations || {}));
    return translations;
};

/**
 * @returns {Promise<{translations: Object|null, errorCode: string|null}>}
 *   translations: { [sourceText]: translatedText } of the data-tr-ondmd markers, null on failure.
 */
export const translateOnDemandSources = async (ticketId, fieldId, sourceTexts) => {
    const { affiliationOrganization, userId } = getRequestIdentity();
    if (!affiliationOrganization || !userId || !ticketId || !fieldId || Utils.isEmptyArray(sourceTexts)) {
        return { translations: null, errorCode: Constant.TRANSLATION_API_ERROR_CODE.INVALID_PARAM };
    }

    const { data, errorCode } = await invokeTranslationUpdateApi({
        affiliationOrganization,
        userId,
        actionType: Constant.TRANSLATION_ACTION.TRANSLATE_ONDEMAND,
        requestBody: {
            sourceType: TranslationConstant.ON_DEMAND_SOURCE_TYPE.HTML_FIELD,
            ticketId,
            fieldId,
            texts: sourceTexts
        }
    });
    if (errorCode) return { translations: null, errorCode };
    return { translations: data?.translations || {}, errorCode: null };
};

/** Session cache key of the on-demand translations of one field. */
export const buildOnDemandCacheKey = (ticketId, fieldId, targetLanguage) => `${ticketId}|${fieldId}|${targetLanguage}`;

/**
 * public/assets/html/{org}/ticket/{ticketId}/{fieldId}.html -> { ticketId, fieldId }.
 * Same parsing as buildShareLinkBusinessApp, for the business app page field.
 */
export const parseHtmlFieldKey = htmlKey => {
    if (!htmlKey || typeof htmlKey !== 'string') return { ticketId: '', fieldId: '' };
    const [, ticketPath = ''] = htmlKey.split('/ticket/');
    const [ticketId = '', fileName = ''] = ticketPath.split('/');
    return { ticketId, fieldId: fileName.replace(/\.html$/, '') };
};

const HtmlTranslationHandler = {
    translateTrSources,
    translateOnDemandSources,
    buildOnDemandCacheKey,
    parseHtmlFieldKey
};

export default HtmlTranslationHandler;
